import { MatDialog } from "@angular/material/dialog";
import type { GridOptions, ColDef } from "ag-grid-enterprise";
import type { RowClickedEvent } from "ag-grid-community/dist/types/core/events";
import { TransactionDescriptionCellRenderer } from "@bitwarden/web-vault/app/components/tables/renderers/cell/transaction-desc-renderer/transaction-desc-renderer.component";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { TransactionDetailsComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/transaction-details/transaction-details.component";
import { BalanceDetailsRendererComponent } from "@bitwarden/web-vault/app/components/tables/renderers/cell/balance-details-renderer/balance-details-renderer.component";
import { BalanceAlignmentTransactionComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/balance-alignment-transaction/balance-alignment-transaction.component";
import { RevaluationTransactionComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/revaluation-transaction/revaluation-transaction.component";
import { REVALUATION } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { isGroceryTransaction } from "@bitwarden/web-vault/app/services/store/data-plugin/transaction-plugin.utils";

/** Group header rows (month / week / ... headers). */
export const GROUP_ROW_HEIGHT = 64;
/** Transaction rows. */
export const DATA_ROW_HEIGHT = 51;

export const getOptions = (dialog: MatDialog, currentView: View): GridOptions<TableRow> => {
  const options: GridOptions<TableRow> = {
    rowDragManaged: false,
    suppressCellFocus: true,
    suppressHorizontalScroll: true,
    columnDefs: getTransactionTableColumns(),
    groupDisplayType: "groupRows",
    getRowHeight: (params) => {
      if (params.node.detail) {
        return undefined;
      }
      if (params.node.group) {
        return GROUP_ROW_HEIGHT;
      }
      return DATA_ROW_HEIGHT;
    },
    groupRowRendererParams: {
      suppressCount: true,
      suppressPadding: true,
    },
  };

  if (currentView === View.PAST) {
    options.masterDetail = true;
    options.detailCellRenderer = "transactionInfoPanel";
    options.detailRowHeight = 210;
    options.isRowMaster = (data: TableRow) => isGroceryTransaction(data?.description);
  }

  if (currentView !== View.FUTURE) {
    options.onRowClicked = (event: RowClickedEvent) => {
      if (event.node.group) {
        event.node.setExpanded(!event.node.expanded);
        return;
      }
      const rowData = event.node.data;
      if (!rowData) {
        return;
      }
      if (rowData.transactionView?.bankImportedBalance != null) {
        BalanceAlignmentTransactionComponent.open(rowData, dialog);
      } else if (rowData.description.includes(REVALUATION)) {
        RevaluationTransactionComponent.open(rowData, dialog);
      } else {
        TransactionDetailsComponent.open(rowData, dialog);
      }
    };
  }

  return options;
};

export const getTransactionTableColumns = (): ColDef<TableRow>[] => {
  return [
    {
      field: "description",
      flex: 1,
      cellRenderer: TransactionDescriptionCellRenderer,
      minWidth: 100,
      cellClass: "truncated-cell",
    },
    {
      field: "amount",
      cellRenderer: BalanceDetailsRendererComponent,
      width: 150,
      // autoHeight: true,
    },
  ];
};

export type TableSource = {
  account: string;
  institution: string;
  origin: string;
  symbol: string;
  amount: number;
  accountView: AccountView;
  transactionView?: TransactionView;
};
export type TableRow = TableSource & {
  description: string;
  balance: number;
  transactionDate: string;
};
